/**
 * deepLinkRouter.js — central router for hustleapp:// deep links.
 *
 * Used by:
 *   • Capacitor App plugin (iOS / Android URL openers)
 *   • Hustle Voice (foreground wake-phrase matches dispatch here)
 *   • Picovoice Porcupine background service (Android only)
 *   • iOS Siri Shortcuts (via App Intents → custom URL scheme)
 *
 * Every wake phrase fetched from /api/voice/wake-phrases carries a
 * `deep_link` field like `hustleapp://eats/scan`. This module maps
 * that to the in-app React Router path and (optionally) fires side
 * effects (open camera, dial 000, etc.).
 */
const SCHEME = "hustleapp://";

/**
 * Default mapping of deep_link host paths → in-app paths.
 * Override or extend in `extraMap` argument at runtime.
 */
const DEFAULT_MAP = {
  "home":                   { path: "/" },
  "eats":                   { path: "/hustleeats" },
  "eats/scan":              { path: "/hustleeats/scan",     side: "open-scanner" },
  "eats/discover":          { path: "/hustleeats" },
  "up":                     { path: "/hustleup" },
  "plaza":                  { path: "/hustleplaza" },
  "property":               { path: "/property" },
  "holidays":               { path: "/hustleholidays" },
  "holidays/hotels":        { path: "/hustleholidays/hotels" },
  "tackle":                 { path: "/tackle" },
  "tackle/fish-map":        { path: "/tackle/fish-map" },
  "directory":              { path: "/hustle-directory" },
  "directory/toilets":      { path: "/hustle-directory?q=toilets" },
  "directory/transport":    { path: "/hustle-directory?q=transport" },
  "emergency":              { path: "/hustle-emergency",    side: "emergency-screen" },
  "hustleshop":             { path: "/hustleshop" },
  "shop":                   { path: "/hustleshop" },
};

export function parseDeepLink(url) {
  if (!url) return null;
  if (!url.startsWith(SCHEME)) {
    // Tolerate https://app.hustleplaza.au/<path> universal links
    try {
      const u = new URL(url);
      if (u.host.endsWith("hustleplaza.au") || u.host.endsWith("hustleplaza.com.au")) {
        return { host: u.pathname.replace(/^\//, "").replace(/\/$/, "") || "home",
                  search: u.search || "" };
      }
    } catch { /* fall through */ }
    return null;
  }
  const rest = url.slice(SCHEME.length);
  // Split off ?query
  const [host, search = ""] = rest.split("?", 2);
  return { host: host.replace(/\/$/, ""), search: search ? `?${search}` : "" };
}

export function resolveTarget(deepLinkOrHost, extraMap = {}) {
  const parsed = typeof deepLinkOrHost === "string" && deepLinkOrHost.includes("://")
    ? parseDeepLink(deepLinkOrHost)
    : { host: String(deepLinkOrHost || "").replace(/^\/+|\/+$/g, ""), search: "" };

  if (!parsed) return null;

  const map = { ...DEFAULT_MAP, ...extraMap };
  const entry = map[parsed.host] || null;
  if (!entry) return null;
  return {
    path: `${entry.path}${parsed.search || ""}`,
    side: entry.side || null,
    host: parsed.host,
  };
}

/**
 * navigateDeepLink — call this from anywhere that receives a deep
 * link or wake-phrase match. Requires a navigate fn (react-router).
 */
export function navigateDeepLink({ deepLink, navigate, onSideEffect }) {
  const target = resolveTarget(deepLink);
  if (!target) {
    console.warn("[deepLinkRouter] unmapped:", deepLink);
    return false;
  }
  try { navigate?.(target.path); } catch (e) { console.warn(e); }
  if (target.side && typeof onSideEffect === "function") {
    try { onSideEffect(target.side, target); } catch (e) { console.warn(e); }
  }
  return true;
}
